import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { loadTallivoCatalog, ALCOHOL, ALCOHOL_NOTE } from "@/lib/tallivoCatalog";
import { chooseHomepageProducts } from "@/lib/homepageProducts";
import { usePriceFormatter } from "@/lib/currency";
import { useCartStore } from "@/stores/cartStore";
import { supabase } from "@/integrations/supabase/client";

export default function FromOurHandsToYours() {
  const fmt = usePriceFormatter();
  const addItem = useCartStore(s => s.addItem);
  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ["homepage-tallivo-products"], staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [catalog, selection] = await Promise.all([loadTallivoCatalog(), supabase.from("homepage_featured_products").select("handle").order("position")]);
      return chooseHomepageProducts(catalog, (selection.data ?? []).map(row => row.handle));
    },
  });
  return <section className="bg-background py-16 md:py-20" aria-labelledby="from-our-hands">
    <div className="container mx-auto px-6">
      <div className="mb-10 text-center">
        <p className="mb-3 font-sans text-xs uppercase tracking-widest text-accent">Made in St. Kitts</p>
        <h2 id="from-our-hands" className="font-serif text-3xl md:text-4xl text-foreground">From Our Hands to Yours</h2>
        <p className="mx-auto mt-4 max-w-2xl font-sans text-muted-foreground">Discover our Caribbean-made essentials for beauty, wellness and everyday living.</p>
      </div>
      {isLoading ? <div className="grid grid-cols-2 gap-6 lg:grid-cols-4" aria-label="Loading products">{Array.from({length:8}, (_, i) => <div key={i} className="aspect-square bg-muted animate-pulse motion-reduce:animate-none" />)}</div>
        : isError ? <p role="status" className="text-center text-muted-foreground">Our selection is temporarily unavailable.</p>
        : <div className="grid grid-cols-2 gap-x-5 gap-y-10 md:gap-x-8 lg:grid-cols-4">
          {products.map(product => {
            const p = product.node;
            const variant = p.variants.edges[0]?.node;
            const quickAdd = p.variants.edges.length === 1 && variant?.availableForSale === true;
            const image = p.images.edges[0]?.node;
            return <article key={p.handle} className="flex min-w-0 flex-col">
              <Link to={`/product/${p.handle}`} className="group block aspect-square overflow-hidden bg-secondary/40" aria-label={p.title}>
                {image ? <img src={image.url} alt={image.altText || p.title} loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none" /> : <ShoppingBag className="mx-auto mt-16 h-12 w-12 text-muted-foreground" />}
              </Link>
              {product.brand && <p className="mt-4 font-sans text-xs uppercase tracking-widest text-accent">{product.brand}</p>}
              <h3 className="mt-2 font-sans text-base font-medium text-foreground"><Link to={`/product/${p.handle}`} className="hover:text-primary">{p.title}</Link></h3>
              <p className="mt-2 font-sans text-sm text-foreground">{p.variants.edges.length > 1 ? "From " : ""}{fmt(p.priceRange.minVariantPrice)}</p>
              {ALCOHOL.test(p.title) && <p className="mt-2 font-sans text-xs text-muted-foreground">{ALCOHOL_NOTE}</p>}
              <div className="mt-auto pt-4">
                {quickAdd && variant ? <Button variant="outline" className="w-full whitespace-normal h-auto min-h-10" onClick={async () => {
                  await addItem({ product, variantId: variant.id, variantTitle: variant.title, price: variant.price, quantity: 1, selectedOptions: variant.selectedOptions });
                  toast.success(`${p.title} added to cart`);
                }}><ShoppingBag className="mr-2 h-4 w-4 shrink-0" />Add to Cart</Button>
                  : <Button asChild variant="outline" className="w-full"><Link to={`/product/${p.handle}`}>View Options<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
              </div>
            </article>;
          })}
        </div>}
      <div className="mt-12 text-center"><Button asChild size="lg"><Link to="/shop">Shop All Products<ArrowRight className="ml-2 h-4 w-4" /></Link></Button></div>
    </div>
  </section>;
}